import { ComponentType, PropsWithChildren } from "react"
import { CustomerType, Product } from "../acquisitionForm/form/useAcquisitionFormSchema"
import { ACQUISITION_FORM_LINKS } from "./links"
import { CustomerTypePage } from "./CustomerTypePage"
import { CUSTOMER_TYPE_ROUTER_KEY, PRODUCT_ROUTER_KEY } from "./utils"
import { ContractTypePage } from "./contractType/ContractTypePage"
import { ContactPage } from "./contact/ContactPage"
import { ConsumptionPointPage } from "./consumptionPoint/ConsumptionPointPage"
import { ProductionPointPage } from "./productionPoint/ProductionPointPage"
import { PaymentPage } from "./payment/PaymentPage"
import { SummaryPage } from "./summary/SummaryPage"
import { assertUnreachable } from "../../../../utils/types"

export type AcquisitionFormStepProps = {
  formVariant?: "primary" | "secondary"
}

export type AcquisitionFormStep = {
  constructUrl: (customerType: CustomerType, product: Product) => string
  routePath: string
  PageComponent: ComponentType<PropsWithChildren<AcquisitionFormStepProps>>
}

const AcquisitionCustomerTypeStep: AcquisitionFormStep = {
  constructUrl: () => ACQUISITION_FORM_LINKS.CUSTOMER_TYPE,
  routePath: ACQUISITION_FORM_LINKS.CUSTOMER_TYPE,
  PageComponent: CustomerTypePage,
}

const AcquisitionContractTypeStep: AcquisitionFormStep = {
  constructUrl: ACQUISITION_FORM_LINKS.CONTRACT_TYPE,
  routePath: ACQUISITION_FORM_LINKS.CONTRACT_TYPE(CUSTOMER_TYPE_ROUTER_KEY),
  PageComponent: ContractTypePage,
}

const AcquisitionContactStep: AcquisitionFormStep = {
  constructUrl: ACQUISITION_FORM_LINKS.CONTACT,
  routePath: ACQUISITION_FORM_LINKS.CONTACT(CUSTOMER_TYPE_ROUTER_KEY, PRODUCT_ROUTER_KEY),
  PageComponent: ContactPage,
}

const AcquisitionConsumptionPointStep: AcquisitionFormStep = {
  constructUrl: ACQUISITION_FORM_LINKS.CONSUMPTION_POINT,
  routePath: ACQUISITION_FORM_LINKS.CONSUMPTION_POINT(CUSTOMER_TYPE_ROUTER_KEY, PRODUCT_ROUTER_KEY),
  PageComponent: ConsumptionPointPage,
}

const AcquisitionProductionPointStep: AcquisitionFormStep = {
  constructUrl: ACQUISITION_FORM_LINKS.PRODUCTION_POINT,
  routePath: ACQUISITION_FORM_LINKS.PRODUCTION_POINT(CUSTOMER_TYPE_ROUTER_KEY, PRODUCT_ROUTER_KEY),
  PageComponent: ProductionPointPage,
}

const AcquisitionPaymentStep: AcquisitionFormStep = {
  constructUrl: ACQUISITION_FORM_LINKS.PAYMENT,
  routePath: ACQUISITION_FORM_LINKS.PAYMENT(CUSTOMER_TYPE_ROUTER_KEY, PRODUCT_ROUTER_KEY),
  PageComponent: PaymentPage,
}

const AcquisitionSummaryStep: AcquisitionFormStep = {
  constructUrl: ACQUISITION_FORM_LINKS.SUMMARY,
  routePath: ACQUISITION_FORM_LINKS.SUMMARY(CUSTOMER_TYPE_ROUTER_KEY, PRODUCT_ROUTER_KEY),
  PageComponent: SummaryPage,
}

const COMPLETE_ACQUISITION_FORM_STEPS: AcquisitionFormStep[] = [
  AcquisitionCustomerTypeStep,
  AcquisitionContractTypeStep,
  AcquisitionContactStep,
  AcquisitionConsumptionPointStep,
  AcquisitionProductionPointStep,
  AcquisitionPaymentStep,
  AcquisitionSummaryStep,
]

const CONSUMPTION_FORM_STEPS: AcquisitionFormStep[] = [
  AcquisitionCustomerTypeStep,
  AcquisitionContractTypeStep,
  AcquisitionContactStep,
  AcquisitionConsumptionPointStep,
  AcquisitionPaymentStep,
  AcquisitionSummaryStep,
]

const PRODUCTION_FORM_STEPS: AcquisitionFormStep[] = [
  AcquisitionCustomerTypeStep,
  AcquisitionContractTypeStep,
  AcquisitionContactStep,
  AcquisitionProductionPointStep,
  AcquisitionPaymentStep,
  AcquisitionSummaryStep,
]

const PRODUCTION_AND_CONSUMPTION_FORM_STEPS: AcquisitionFormStep[] = [
  AcquisitionCustomerTypeStep,
  AcquisitionContractTypeStep,
  AcquisitionContactStep,
  AcquisitionConsumptionPointStep,
  AcquisitionProductionPointStep,
  AcquisitionPaymentStep,
  AcquisitionSummaryStep,
]

export function getCompleteAcquisitionFormSteps() {
  return COMPLETE_ACQUISITION_FORM_STEPS
}

export function getAcquisitionFormSteps(product: Product | null) {
  switch (product) {
    case "consumption":
    case null:
      return CONSUMPTION_FORM_STEPS
    case "production":
      return PRODUCTION_FORM_STEPS
    case "productionAndConsumption":
      return PRODUCTION_AND_CONSUMPTION_FORM_STEPS
    default:
      return assertUnreachable(product)
  }
}
